import { useEffect, useState } from 'react';
import type { AdminLureTypeResponse } from '../../types/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  open: boolean;
  lureType: AdminLureTypeResponse | null;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export function LureTypeFormDialog({ open, lureType, onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(lureType?.name ?? '');
      setError(null);
    }
  }, [open, lureType]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    if (name.trim().length > 50) { setError('Name must be 50 characters or fewer.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(name.trim());
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save lure.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {lureType ? 'Edit Lure' : 'New Lure'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="e.g. Crankbait" maxLength={50} />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
